import clsx from 'clsx';
import {
  ChevronDown,
  FileAudio,
  FolderOpen,
  Loader2,
  Scissors,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { importAudioFile, registerBuffer } from '../audio/decode';
import { sliceCount } from '../audio/slice';
import { DEMO_KITS, renderKit, type DemoKit } from '../lib/demo';
import { importPackBreak, loadBreakPack, type PackBreak } from '../lib/packs';
import { useStore } from '../store/useStore';
import type { SourceMeta } from '../types';

/** Left rail: import, list, and select loops. Drop files anywhere on it. */
export function SourceLibrary({ onFindLoops }: { onFindLoops: () => void }) {
  const project = useStore((s) => s.project);
  const selectedSourceId = useStore((s) => s.selectedSourceId);
  const setUi = useStore((s) => s.setUi);
  const importSource = useStore((s) => s.importSource);
  const removeSource = useStore((s) => s.removeSource);
  const notify = useStore((s) => s.notify);

  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showKits, setShowKits] = useState(project.sources.length === 0);
  const [showLoad, setShowLoad] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);

  // `webkitdirectory` isn't in React's typed attribute list, so it goes on the
  // node directly. Selecting a folder keeps the audio on this machine — nothing
  // is uploaded and nothing is bundled into the app.
  useEffect(() => {
    const el = folderRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);

  /**
   * Lands a freshly imported loop on the first empty track with a 1:1 pattern,
   * so you hear something immediately instead of staring at an empty grid.
   */
  const place = useCallback(
    (meta: SourceMeta) => {
      importSource(meta, true);
      notify(`${meta.name} · ${meta.bpm.toFixed(1)} BPM detected`);
    },
    [importSource, notify],
  );

  const ingest = useCallback(
    async (files: FileList | File[]) => {
      setLoading(true);
      try {
        for (const file of Array.from(files)) {
          if (!/\.(wav|mp3|ogg|flac|m4a|aac|aiff?)$/i.test(file.name)) continue;
          const { meta } = await importAudioFile(file, useStore.getState().project.beatsPerBar);
          place(meta);
        }
      } catch (err) {
        notify(`Import failed: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    },
    [notify, place],
  );

  /** Kits are generated, so their tempo is known exactly — no need to detect it. */
  /** Real audio from `public/breaks/`, if any has been dropped in. */
  const [packs, setPacks] = useState<PackBreak[]>([]);
  useEffect(() => {
    void loadBreakPack().then(setPacks);
  }, []);

  const loadPack = useCallback(
    async (entry: PackBreak) => {
      setLoading(true);
      try {
        const bpb = useStore.getState().project.beatsPerBar;
        place(await importPackBreak(entry, bpb));
        setUi({ view: 'sequence' });
      } catch (err) {
        notify(`Couldn't load ${entry.name}: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    },
    [place, setUi, notify],
  );

  const loadKit = useCallback(
    (kit: DemoKit) => {
      const bpb = useStore.getState().project.beatsPerBar;
      const { meta } = registerBuffer(renderKit(kit), kit.name, bpb, { bpm: kit.bpm });
      place(meta);
      // A kit's tempo and grid are known, so there's nothing to check in the
      // chop view — go straight to where you'd actually play with it.
      setUi({ view: 'sequence' });
    },
    [place, setUi],
  );

  return (
    <div
      className={clsx(
        'flex h-full w-60 shrink-0 flex-col gap-2 border-r border-ink-700 bg-ink-900 p-3 transition-colors',
        dragOver && 'bg-volt-500/5 ring-1 ring-inset ring-volt-500/40',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        void ingest(e.dataTransfer.files);
      }}
    >
      <div className="flex items-center justify-between">
        <span className="label">Loops</span>
        <span className="mono text-[10px] text-ink-500">{project.sources.length}</span>
      </div>

      {/* Lifting a loop out of a track is the way in — importing an already-cut
          loop is the exception, not the default. */}
      <button
        className="btn btn-primary justify-center py-2"
        data-tour="find-loops"
        onClick={onFindLoops}
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Scissors size={13} />}
        Find loops in a track
      </button>

      {/* Loading samples is the occasional job — collapsed by default so the
          column stays about the loops you already have. */}
      <button
        className={clsx('btn justify-center', showLoad && 'btn-active')}
        onClick={() => setShowLoad((v) => !v)}
      >
        <Upload size={12} />
        Load samples
        <ChevronDown
          size={12}
          className={clsx('ml-auto transition-transform', showLoad && 'rotate-180')}
        />
      </button>

      {/* max-height rather than the 0fr/1fr grid trick. This column is a
          height-constrained flex container, so `1fr` resolved against leftover
          space (about 5px) instead of the content. max-height has no such
          dependency; the ceiling just needs to clear the content. */}
      <div
        className="shrink-0 overflow-hidden"
        style={{
          maxHeight: showLoad ? '12rem' : '0rem',
          opacity: showLoad ? 1 : 0,
          marginTop: showLoad ? undefined : '-0.5rem', // swallow the flex gap when shut
          transition: 'max-height 220ms cubic-bezier(.4,0,.2,1), opacity 180ms ease, margin-top 220ms',
        }}
      >
        <div className="min-h-0">
          <div className="flex flex-col gap-2 pt-2">
            <button
              className="btn justify-center text-[11px]"
              onClick={() => inputRef.current?.click()}
              title="Load a loop that's already cut — drag and drop works too"
            >
              <Upload size={12} />
              Import a loop
            </button>

            <button
              className="btn justify-center text-[11px]"
              onClick={() => folderRef.current?.click()}
              title="Point at a folder of breaks on this machine — nothing is uploaded"
            >
              <FolderOpen size={12} />
              Load a folder
            </button>
          </div>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.wav,.mp3,.flac,.ogg,.m4a"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) void ingest(e.target.files);
          e.target.value = '';
        }}
      />

      {/* A whole sample folder — the way to use a break library you already own
          without shipping any of it. Files are read locally; none are uploaded. */}
      <input
        ref={folderRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const all = Array.from(e.target.files ?? []);
          const audio = all.filter((f) =>
            /\.(wav|mp3|ogg|flac|m4a|aac|aiff?)$/i.test(f.name),
          );
          if (audio.length === 0) {
            notify('No audio files in that folder');
          } else {
            const skipped = all.length - audio.length;
            notify(
              `Loading ${audio.length} file${audio.length === 1 ? '' : 's'}` +
                (skipped > 0 ? ` — skipped ${skipped} non-audio` : ''),
            );
            void ingest(audio);
          }
          e.target.value = '';
        }}
      />

      <button
        className={clsx('btn justify-center', showKits && 'btn-active')}
        onClick={() => setShowKits((v) => !v)}
      >
        <Sparkles size={12} />
        Breaks library
        <ChevronDown
          size={12}
          className={clsx('ml-auto transition-transform', showKits && 'rotate-180')}
        />
      </button>

      {showKits && (
        <div className="rise max-h-64 space-y-1 overflow-y-auto rounded-lg border border-ink-700 bg-ink-850 p-1">
          {packs.length > 0 && (
            <div className="label px-1 pt-0.5">Sample pack</div>
          )}
          {packs.map((entry, i) => (
            <button
              key={entry.id}
              onClick={() => void loadPack(entry)}
              style={{ '--i': i } as React.CSSProperties}
              title={[entry.description, entry.credit, entry.source].filter(Boolean).join('\n')}
              className="slide-in group w-full rounded-md px-2 py-1.5 text-left transition-colors hover:translate-x-0.5 hover:bg-volt-500/10"
            >
              <div className="flex items-baseline gap-1.5">
                <span className="flex-1 truncate text-[11px] font-medium group-hover:text-volt-400">
                  {entry.name}
                </span>
                {entry.bpm && <span className="mono text-[9px] text-ink-400">{entry.bpm}</span>}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="flex-1 truncate text-[9px] leading-tight text-ink-500">
                  {entry.credit ?? entry.description ?? ''}
                </span>
                {entry.licence && (
                  <span className="mono shrink-0 rounded bg-ink-700 px-1 text-[8px] text-ink-300">
                    {entry.licence}
                  </span>
                )}
              </div>
            </button>
          ))}

          {packs.length > 0 && <div className="label px-1 pt-1">Synthesised</div>}
          {DEMO_KITS.map((kit, i) => (
            <button
              key={kit.id}
              onClick={() => loadKit(kit)}
              style={{ '--i': i } as React.CSSProperties}
              className="slide-in group w-full rounded-md px-2 py-1.5 text-left transition-colors hover:translate-x-0.5 hover:bg-volt-500/10"
            >
              <div className="flex items-baseline gap-1.5">
                <span className="flex-1 truncate text-[11px] font-medium group-hover:text-volt-400">
                  {kit.name}
                </span>
                <span className="mono text-[9px] text-ink-400">{kit.bpm}</span>
              </div>
              <div className="truncate text-[9px] leading-tight text-ink-500">
                {kit.description}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-0.5">
        {project.sources.length === 0 && !showKits && (
          <p className="mt-6 px-1 text-center text-[11px] leading-relaxed text-ink-500">
            Start with a whole track and lift a few bars out of it. Already have a loop? Import it or
            drop it here — or grab one from the breaks library above.
          </p>
        )}
        {project.sources.map((src) => {
          const active = src.id === selectedSourceId;
          return (
            <div
              key={src.id}
              onClick={() => setUi({ selectedSourceId: src.id, view: 'chop' })}
              className={clsx(
                'group cursor-pointer rounded-lg border px-2 py-1.5 transition-colors',
                active
                  ? 'border-volt-500/60 bg-volt-500/10'
                  : 'border-ink-700 bg-ink-850 hover:border-ink-500',
              )}
            >
              <div className="flex items-center gap-1.5">
                <FileAudio size={12} style={{ color: src.color }} className="shrink-0" />
                <span className="flex-1 truncate text-[11px] font-medium">{src.name}</span>
                <button
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  title="Remove loop"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSource(src.id);
                  }}
                >
                  <Trash2 size={11} className="text-ink-400 hover:text-rose-hot" />
                </button>
              </div>
              <div className="mono mt-0.5 flex gap-2 text-[9px] text-ink-400">
                <span>{src.bpm.toFixed(1)} BPM</span>
                <span>{src.bars} bar</span>
                <span>{sliceCount(src, project.beatsPerBar)} sl</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
